/* eslint-disable react/jsx-max-depth */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { idLib } from '@larki/mp';
import { useClient } from '../hooks/useClient';

import { Buffer } from 'buffer';

import { useAppDispatch } from '@/hooks/useAppDispatch';
import { MpDoc } from '@larki/mp/doc';
import { Alert, OverlayTrigger, Tooltip } from 'react-bootstrap';
import Icon from '../../components/icon/Icon';
import Loading from '../../components/loading/Loading';
import Page from '../../components/page/Page';
import CtaButton from '../../components/RightMenu/CtaButton';
import { MEMBERSHIP } from '../../constants/membership';
import { useAppSelector } from '../../hooks/useAppSelector';
import { Quote2dMapInputs, useQuote2dMap } from '../../hooks/useQuote2dMap';
import type { CustomOrderQuoteItem } from '@larki/shared/type/order.v2';
import {
  toCustomOrderQuoteItems,
  applyOverridesToCustomOrders,
} from '@/hooks/useQuote2dMap';
import { setMembershipPriceId } from '../../redux/actions';
import {
  useCommitRequestForQuoteMutation,
  useGet2dMapStateFromJobMutation,
  useGetJobQuery,
  useGetPriceOverridesQuery,
  useLazyGetCombinedQuoteForQuoteQuery,
  useSetMapLockMutation,
} from '../../redux/rtk';
import { setQuotes } from '../../redux/reducers/order.v2';
import { orderV2Selectors } from '../../redux/selectors/order.v2';
import { applyCommitReprice } from '../hooks/useCommitQuote';
import text, { formatCurrency } from '../../text';
import { isMembershipPriceId } from '../../utilities/membership';
import ForQuotation from '../components/Payment/ForQuotation';
import { InvoiceTotal } from '../components/Payment/InvoiceTotal';
import MembershipInvoice from '../components/Payment/MembershipInvoice';
import OrderWizard, {
  ORDER_STEPS,
  ORDER_STEPS_SEQUENCE,
} from '../components/Payment/OrderWizard';
import ProductInvoice from '../components/Payment/ProductInvoice';
import { PaymentContext } from '../hooks/usePaymentContext';
import useUrlParam from '../hooks/useUrlParam';
import useUser from '../hooks/useUser';
import { useOnCloseTab } from '@/hooks/useOnCloseTab';
// @ts-expect-error react-use-wizard missing WizardValues type
import { WizardValues } from 'react-use-wizard';
import NavBar from '../components/NavBar';
import RealtimeInfoBanner, {
  formatVersionCreatedMessage,
} from '../components/RealtimeInfoBanner';
import useRedirectToMap from '../hooks/useRedirectToMap';
import { captureError } from '@/utilities/error';
import routes from '@/routes/constants';
import { Button } from '@/components/button';
import useQueryParam from '../hooks/useQueryParam';

const InvalidPaymentRoute = ({ jobIdParam }: { jobIdParam: number | null }) => {
  const { redirectToMap } = useRedirectToMap();
  useEffect(() => {
    captureError(new Error('PaymentPage: missing/invalid jobId'), {
      tags: {
        area: 'mp',
        page: 'payment',
        reason: 'invalid_jobId',
      },
      extra: {
        jobIdParam,
      },
    });
  }, [jobIdParam]);

  return (
    // @ts-expect-error
    <Page className='Payment d-flex min-vh-100 align-items-center justify-content-center'>
      <div className='w-100' style={{ maxWidth: 560 }}>
        <Alert variant='danger' className='mb-0'>
          <div className='p-3'>
            <Alert.Heading>{text('orderCouldNotPlaced')}</Alert.Heading>
            <p className='mb-3'>{text('pleaseTryAgainApologise')}</p>

            <div className='d-flex flex-row justify-content-end pt-3'>
              <Link className='btn btn-primary mr-2' to={routes.dashboard}>
                {text('goToDashboard')}
              </Link>
              <Button
                type='button'
                onClick={redirectToMap}
                className='btn btn-outline-primary'
              >
                {text('returnToMap')}
              </Button>
            </div>
          </div>
        </Alert>
      </div>
    </Page>
  );
};

/**
 * Listener-only socket so users on the Payment flow screens still receive
 * "another user created a new map version" broadcasts. Connects to the same
 * room as mp-crdt's full client; mp-server emits via socket.broadcast, so
 * sender's socket is skipped.
 */
const PaymentRoomListener = ({
  map2dId,
  clientName,
  onVersionCreated,
}: {
  map2dId: number;
  clientName?: string;
  onVersionCreated: (message: string) => void;
}) => {
  const clientIdRef = useRef<string>('');
  if (!clientIdRef.current) clientIdRef.current = idLib.shortId();
  const { client } = useClient({
    clientId: clientIdRef.current,
    roomId: `${map2dId}`,
    clientName,
    // mp-server skips doc init + SYNC packet for listenerOnly sockets — we
    // only want to receive room broadcasts here, not a full doc transfer.
    listenerOnly: '1',
  });

  useEffect(() => {
    client.setEventListener('versionCreatedBroadcast', ({ actorName }) => {
      onVersionCreated(formatVersionCreatedMessage(actorName));
    });
  }, [client, onVersionCreated]);

  return null;
};

const PaymentPage = () => {
  const jobIdParam = useUrlParam('jobId', { int: true });
  const jobId = Number.isNaN(jobIdParam) ? null : jobIdParam; // avoid NaN

  if (!jobId) {
    return <InvalidPaymentRoute jobIdParam={jobIdParam} />;
  }

  return <PaymentPageContent jobId={jobId} />;
};

const PaymentPageContent = ({ jobId }: { jobId: number }) => {
  const dispatch = useAppDispatch();
  const currentUser = useUser();

  // Set when arriving from a "Pay Invoice" link: pay this issued invoice's quote
  // at its locked price instead of committing a fresh one (works cross-user).
  const payQuoteIdParam = useQueryParam('payQuoteId');
  const payQuoteId = payQuoteIdParam ? Number(payQuoteIdParam) : null;
  const [loadCombinedQuoteForQuote] = useLazyGetCombinedQuoteForQuoteQuery();

  // Hydrate membership from query param (when coming from "Pay Invoice" link)
  const membershipProductParam = useQueryParam('membership');
  useEffect(() => {
    if (membershipProductParam === 'essentials_monthly') {
      dispatch(setMembershipPriceId(MEMBERSHIP.MONTHLY.PRICE_ID));
    } else if (membershipProductParam === 'essentials_yearly') {
      dispatch(setMembershipPriceId(MEMBERSHIP.YEARLY.PRICE_ID));
    }
  }, [membershipProductParam, dispatch]);

  const doQuotingAsMember =
    currentUser.isEssentialUser || currentUser.isGovernment;
  const membershipPriceId = useAppSelector(
    (state) => state.order.membershipPriceId
  );
  const asMember = useMemo(
    () => doQuotingAsMember || isMembershipPriceId(membershipPriceId),
    [doQuotingAsMember, membershipPriceId]
  );
  const quote = useAppSelector((state) =>
    orderV2Selectors.getQuote(state, jobId, asMember)
  );
  const memberQuote = useAppSelector((state) =>
    orderV2Selectors.getQuote(state, jobId, true)
  );
  const nonRfqCharges = useAppSelector((state) =>
    orderV2Selectors.getNonRfqCharges(state, jobId, asMember)
  );

  const [get2dMapStateFromJob] = useGet2dMapStateFromJobMutation();
  const [setMapLock] = useSetMapLockMutation();
  const [commitRequestForQuote] = useCommitRequestForQuoteMutation();
  const { data: job } = useGetJobQuery(jobId, {
    skip: !jobId,
  });
  const { data: activePriceOverrides = [] } = useGetPriceOverridesQuery(
    { jobId },
    { skip: !jobId }
  );

  const [
    getSavedOrLatest2dMapQuoteLoading,
    setGetSavedOrLatest2dMapQuoteLoading,
  ] = useState(false);
  const { doQuote, quoteShapes, getCheckoutState } = useQuote2dMap(
    setGetSavedOrLatest2dMapQuoteLoading
  );

  const [latestInputs, setLatestInputs] = useState<Quote2dMapInputs | null>(
    null
  );
  const [customOrderItems, setCustomOrderItems] = useState<
    CustomOrderQuoteItem[]
  >([]);
  const customOrderItemsWithOverrides = useMemo(
    () => applyOverridesToCustomOrders(customOrderItems, activePriceOverrides),
    [customOrderItems, activePriceOverrides]
  );
  const [realtimeBannerMessage, setRealtimeBannerMessage] = useState<
    string | null
  >(null);

  const [committedQuoteId, setCommittedQuoteId] = useState<number | null>(null);
  const [committedRequestForQuoteId, setCommittedRequestForQuoteId] = useState<
    number | null
  >(null);
  const [lastCommittedMembershipPriceId, setLastCommittedMembershipPriceId] =
    useState<string | null>(null);
  /**
   * === QUOTE ===
   *
   * Create the RFQ as soon as the payment page is reached.
   * Create the Quote once the payment **step** is reached.
   */

  useEffect(() => {
    /**
     * We quote in two phases:
     *
     * 1. Shapes -> Inputs
     *
     * First, we need to convert our shapes (polygons, rectangles, paths)
     * into `layers` that represent the products we need to quote.
     * We also need the locality to price our site deployment fee.
     *
     * 2. Inputs -> Quote
     *
     * Finally, we can use the inputs to get a quote.
     *
     * The two-phase approach allows us to cache the inputs and reuse them.
     *
     */
    async function initQuote() {
      if (!job) {
        return null;
      }
      const checkoutState = await getCheckoutState(jobId);

      let doc: MpDoc | null = null;
      if (checkoutState?.snapshot) {
        try {
          doc = MpDoc.fromSnapshot({ state: checkoutState.snapshot });
        } catch (err) {
          // A stored snapshot can be corrupt (driver fallback) or from an
          // incompatible doc version after a deploy; rebuild from the server
          // instead of aborting the RFQ.
          captureError(err, {
            tags: { area: 'mp', flow: 'checkout_snapshot' },
            extra: { jobId },
          });
        }
      }
      if (!doc) {
        const _2dMapState = await get2dMapStateFromJob({ jobId }).unwrap();
        if (!_2dMapState.bytes) {
          throw new Error('No 2D map state found');
        }
        doc = MpDoc.fromSnapshot({
          state: new Uint8Array(Buffer.from(_2dMapState.bytes, 'base64')),
        });
        if (checkoutState?.frontiers) {
          try {
            doc.checkout(checkoutState.frontiers);
          } catch (err) {
            // Frontiers can reference ops missing from the lagging server
            // snapshot; degrade to latest instead of aborting the RFQ.
            doc.checkoutToLatest();
            captureError(err, {
              tags: { area: 'mp', flow: 'checkout_frontiers' },
              extra: { jobId },
            });
          }
        }
      }
      const shapes = doc.getShapes();

      const allCustomOrders = doc.getCustomOrders();
      const usedOrderIds = new Set(
        shapes.map((s) => s.customOrderId).filter(Boolean)
      );
      const usedOrders = allCustomOrders.filter((o) => usedOrderIds.has(o.id));
      const coItems: CustomOrderQuoteItem[] =
        toCustomOrderQuoteItems(usedOrders);
      setCustomOrderItems(coItems);

      const { inputs } = await quoteShapes(
        jobId,
        shapes,
        membershipPriceId,
        job?.place_geometry,
        {
          versionId: checkoutState?.versionId ?? null,
          customOrders: coItems,
          honorPriceLock: true,
        }
      );
      return inputs;
    }

    let isMounted = true;

    // Pay Invoice: show the invoiced quote's locked snapshot; never re-quote
    // live (that would show current prices to a collaborator paying).
    if (payQuoteId) {
      loadCombinedQuoteForQuote({ quoteId: payQuoteId })
        .unwrap()
        .then((combined) => {
          if (!isMounted) return;
          dispatch(
            setQuotes({
              jobId,
              freeUserQuote: combined,
              memberQuote: combined,
            })
          );
        })
        .catch((err: { status?: number }) => {
          if (!isMounted) return;
          captureError(err);
          setRealtimeBannerMessage(
            err?.status === 403
              ? text('payInvoiceNoAccess')
              : text('payInvoiceLoadFailed')
          );
        });
      return () => {
        isMounted = false;
      };
    }

    initQuote()
      .then((inputs) => {
        if (isMounted) setLatestInputs(inputs);
      })
      .catch((err) => {
        console.error('Init quote failed:', err);
      });

    return () => {
      isMounted = false;
    };
  }, [
    get2dMapStateFromJob,
    getCheckoutState,
    job?.place_geometry,
    jobId,
    job,
    membershipPriceId,
    quoteShapes,
    payQuoteId,
    loadCombinedQuoteForQuote,
    dispatch,
  ]);

  useEffect(() => {
    if (!latestInputs) return;
    if (!job) return;

    let isMounted = true;

    const run = async () => {
      try {
        const checkoutState = await getCheckoutState(jobId);
        if (!isMounted) return;

        const versionStatus = checkoutState?.versionStatus ?? null;
        const status = versionStatus ?? job.status;
        const isEditableVersion = status === 'draft' || status === 'pending';
        if (!isEditableVersion) return;

        const { freeUserQuote, memberQuote, quoteInputs } = await doQuote(
          jobId,
          latestInputs,
          {
            membershipPriceId,
            customOrders: customOrderItemsWithOverrides,
            map2dVersionId: checkoutState?.versionId,
            honorPriceLock: true,
          }
        );
        if (!isMounted) return;

        const rfq = asMember ? memberQuote : freeUserQuote;
        const { rfqId, quote: repriced } = await commitRequestForQuote({
          jobId,
          data: { rfq, quoteInputs: { ...quoteInputs, asMember } },
        }).unwrap();

        if (!isMounted) return;
        applyCommitReprice(dispatch, { jobId, asMember, repriced });
        setCommittedRequestForQuoteId(rfqId);
      } catch (err) {
        if (isMounted) {
          console.error('doQuote failed:', err);
        }
      }
    };

    run();

    return () => {
      isMounted = false;
    };
  }, [
    asMember,
    commitRequestForQuote,
    customOrderItemsWithOverrides,
    dispatch,
    doQuote,
    getCheckoutState,
    job,
    jobId,
    latestInputs,
    membershipPriceId,
  ]);

  /**
   * === MAP LOCK ===
   *
   * Lock the map while the user is checking out.
   */

  useEffect(() => {
    // Pay-invoice mode has no live cart to protect; leave the owner's lock alone.
    if (payQuoteId) return;
    setMapLock({
      params: { jobId },
      reqBody: { locked: true, mode: 'checkout' },
    });
    return () => {
      setMapLock({
        params: { jobId },
        reqBody: { locked: false },
      });
    };
  }, [jobId, setMapLock, payQuoteId]);

  useOnCloseTab(
    useCallback(() => {
      if (payQuoteId) return;
      setMapLock({
        params: { jobId },
        reqBody: { locked: false },
      });
    }, [jobId, setMapLock, payQuoteId])
  );

  const isLoadingQuote = useMemo(
    () => getSavedOrLatest2dMapQuoteLoading && !quote,
    [getSavedOrLatest2dMapQuoteLoading, quote]
  );

  const formatPrice = useCallback(
    (price: number) => {
      if (!quote?.currency) return '';
      return formatCurrency(
        price,
        quote?.currency.name,
        quote?.currency.scale_factor,
        {},
        true
      );
    },
    [quote]
  );

  // const dispatch = useDispatch();
  // const [quoteOrderMutation] = useQuoteOrderMutation();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- legacy
  // const { membershipPriceId, membershipPrices } = useSelector((state: any) => ({
  //   membershipPriceId: state.order.membershipPriceId,
  //   membershipPrices: state.profileReducer.membershipPrices,
  // }));

  // const [cardHeading] = useState(text('estimate'));

  // // eslint-disable-next-line @typescript-eslint/no-explicit-any -- legacy
  // const otherPaymentContext: any = useContext(OtherPaymentContext);
  // const {
  //   states: { activeWizardStep },
  //   refs: { wizzardFunctions },
  // } = otherPaymentContext;

  // const cartItemsAutomatic = useMemo(
  //   () =>
  //     job?.quote?.charges.filter(
  //       (charge) => charge.details?.delivery_method === 'automatic'
  //     ),
  //   [job?.quote]
  // );

  // const selectedMembership = useMemo(
  //   () =>
  //     membershipPrices.value?.find(
  //       (membership: { id: string }) => membership.id === membershipPriceId
  //     ),
  //   [membershipPriceId, membershipPrices]
  // );

  // const numBillableItems = useMemo(
  //   () =>
  //     [cartItemsAutomatic?.length, selectedMembership].filter(Boolean).length,
  //   [cartItemsAutomatic?.length, selectedMembership]
  // );

  // useEffect(() => {
  //   dispatch(setMembershipPriceId(MEMBERSHIP.NO_MEMBERSHIP));
  //   dispatch(getMembershipPrices());
  //   dispatch(getUserProfile());
  //   dispatch(getJob(jobId));
  // }, [dispatch, jobId]);

  // const isFreeUser = useMemo(() => {
  //   if (isUserGovernment(currentUser.profile)) return false;
  //   return !currentUser.hasValidMembership || currentUser.isUserUnderTrial;
  // }, [currentUser]);

  // const isInProjectStep = activeWizardStep && activeWizardStep.step === 2;

  // const memberBuyNowDiscount = useMemo(() => {
  //   if (!job?.quote) {
  //     return null;
  //   }
  //   const { gst = GST, price, currency } = job.quote;
  //   return formatCurrency(
  //     price.member_automatic_discount_total * (1 + gst / 100),
  //     currency.name,
  //     currency.scale_factor,
  //     {},
  //     true
  //   );
  // }, [job?.quote]);

  const membershipDiscount = useMemo(() => {
    if (!memberQuote) return null;
    return memberQuote.nonRfq.totals.discountsTotal;
  }, [memberQuote]);

  // const quoteRequestedRef = useRef<string | null>(null);
  // // Run quote once when in Payment page, to consolidate totals are accurate for the current user
  // useEffect(() => {
  //   const handleQuoteOrder = async () => {
  //     if (!job?.id || !job.user_order?.selections || !currentUser.profile?.id) {
  //       return;
  //     }
  //     const requestKey = `${job.id}-${currentUser.profile.id}`;
  //     if (quoteRequestedRef.current !== requestKey) {
  //       quoteRequestedRef.current = requestKey;

  //       try {
  //         const response = await quoteOrderMutation({
  //           jobId,
  //           selections: job.user_order.selections,
  //           includeFreeTierDiscount: true,
  //           property: null,
  //           shouldSetUpdater: true,
  //           invalidateProject: true,
  //         }).unwrap();

  //         // update legacy state
  //         dispatch({
  //           type: jobsConstants.QUOTE_ORDER_SUCCESS,
  //           payload: response,
  //         });
  //       } catch (error) {
  //         console.error('Quote order failed:', error);
  //         dispatch(showModal(QUOTING_FAILURE_MODAL));
  //       }
  //     }
  //   };

  //   handleQuoteOrder();
  // }, [
  //   currentUser.profile?.id,
  //   dispatch,
  //   job?.id,
  //   job?.user_order?.selections,
  //   jobId,
  //   quoteOrderMutation,
  // ]);

  const invoiceSection = <ProductInvoice />;
  const membershipSection = <MembershipInvoice />;
  const wizardFunctions = useRef<WizardValues | null>(null);

  const isMembershipCtaVisible =
    !currentUser.isGovernment &&
    // don't show if we're already a member
    !asMember &&
    // don't show if we have already selected a membership
    !isMembershipPriceId(membershipPriceId) &&
    // don't show on the confirmation step
    wizardFunctions.current?.activeStep !==
      ORDER_STEPS_SEQUENCE.indexOf(ORDER_STEPS.CONFIRMATION) &&
    // don't show on admin/staff
    !currentUser.isStaff;

  // no aerial, streetscape or membership means order is an RFQ
  // unless there are overridden rfq items (which move to BUY NOW)
  const hasOverriddenItems = (quote?.overriddenProducts?.length ?? 0) > 0;
  const isRfqOrder = nonRfqCharges?.length === 0 && !hasOverriddenItems;

  const { redirectToMap } = useRedirectToMap();

  return (
    <div className='position-relative d-flex flex-column h-100 w-100'>
      {job?.map2d_id ? (
        <PaymentRoomListener
          map2dId={job.map2d_id}
          clientName={currentUser.profile?.full_name ?? undefined}
          onVersionCreated={setRealtimeBannerMessage}
        />
      ) : null}
      <NavBar.Provider>
        <NavBar.Toggle />
        <NavBar.Brand />
        <NavBar.UserNav />
      </NavBar.Provider>

      <RealtimeInfoBanner
        message={realtimeBannerMessage}
        onDismiss={() => setRealtimeBannerMessage(null)}
        autoDismiss={false}
        inline
      />

      <PaymentContext.Provider
        value={{
          jobId,
          isLoadingQuote,
          formatPrice,
          asMember,
          wizardFunctions,
          committedQuoteId,
          setCommittedQuoteId,
          payQuoteId,
          committedRequestForQuoteId,
          isRfqOrder,
          lastCommittedMembershipPriceId,
          setLastCommittedMembershipPriceId,
        }}
      >
        {/* @ts-expect-error */}
        <Page className='Payment'>
          <div className='main-wrapper'>
            <div className='invoice-wrapper'>
              <div className='invoice-section'>
                <div className='d-flex'>
                  <Link
                    onClick={(e) => {
                      e.preventDefault();
                      redirectToMap();
                    }}
                    className='d-flex align-items-center'
                    to='#'
                  >
                    <Icon icon='caret-left' />
                    {text('backTo2DMap')}:
                  </Link>
                </div>

                {isLoadingQuote ? <Loading /> : null}

                {invoiceSection || membershipSection ? (
                  <>
                    {nonRfqCharges?.length > 0 || hasOverriddenItems ? (
                      <p className='invoice-title text-uppercase font-16 mb-3'>
                        {text('buyNow')}:
                      </p>
                    ) : null}
                    {invoiceSection}
                    {membershipSection}
                  </>
                ) : null}

                {nonRfqCharges?.length > 0 || hasOverriddenItems ? (
                  <InvoiceTotal />
                ) : (
                  <div className='mt-3' />
                )}

                {isMembershipCtaVisible ? (
                  <CtaButton
                    className='mb-2'
                    show
                    header={text('membership')}
                    description={text('ctaMembershipSubtitle', {
                      amount: `—${formatPrice(membershipDiscount ?? 0)}`,
                    })}
                    titleClassNames='pl-2 font-14'
                    subTitleClassNames='pl-2 pink font-14 font-italic mt-2'
                    isTertiary
                    // @ts-expect-error prop
                    tooltip={
                      <OverlayTrigger
                        trigger={['hover', 'focus']}
                        placement='top'
                        overlay={
                          <Tooltip id='other-charge-tooltip'>
                            {text('ctaMembershipTooltip')}
                          </Tooltip>
                        }
                      >
                        <Icon
                          icon='question'
                          className='other-charge-icon'
                          style={{ pointerEvents: 'auto' }}
                        />
                      </OverlayTrigger>
                    }
                    handleClick={() => {
                      dispatch(
                        setMembershipPriceId(MEMBERSHIP.YEARLY.PRICE_ID)
                      );
                      wizardFunctions.current?.goToStep(
                        ORDER_STEPS_SEQUENCE.indexOf(ORDER_STEPS.MEMBER)
                      );
                    }}
                  />
                ) : null}

                <ForQuotation />
              </div>
            </div>

            <div className='order-wizard'>
              <div className='section'>
                <OrderWizard isRfqOrder={isRfqOrder} />
              </div>
            </div>
          </div>
        </Page>
      </PaymentContext.Provider>
    </div>
  );
};

export default PaymentPage;
